"use client";

import Script from "next/script";
import { useCallback, useEffect, useRef, useState } from "react";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";

type TurnstileOptions = {
  sitekey: string;
  action: string;
  language: Locales;
  callback: (token: string) => void;
  "error-callback": (errorCode: string) => boolean;
  "expired-callback": () => void;
  "timeout-callback": () => void;
};

type TurnstileClient = {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileClient;
  }
}

type TurnstileWidgetProps = {
  locale: Locales;
  onTokenChange: (token: string | null) => void;
  resetKey: number;
  siteKey?: string;
};

const TurnstileWidget = ({ locale, onTokenChange, resetKey, siteKey }: TurnstileWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [hasError, setHasError] = useState(!siteKey);

  const resetWidget = useCallback(() => {
    onTokenChange(null);
    setHasError(false);

    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [onTokenChange]);

  const renderWidget = useCallback(() => {
    if (!siteKey || !containerRef.current || !window.turnstile || widgetIdRef.current) return;

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      action: "contact",
      language: locale,
      callback: (token) => {
        setHasError(false);
        onTokenChange(token);
      },
      "error-callback": () => {
        setHasError(true);
        onTokenChange(null);
        return true;
      },
      "expired-callback": resetWidget,
      "timeout-callback": resetWidget,
    });
  }, [locale, onTokenChange, resetWidget, siteKey]);

  useEffect(() => {
    renderWidget();

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget]);

  useEffect(() => {
    if (resetKey > 0) resetWidget();
  }, [resetKey, resetWidget]);

  return (
    <div className="space-y-2.5">
      <p className="text-base font-medium">
        {__("Security verification", locale)} <span className="text-pink-primary">*</span>
      </p>
      {siteKey && (
        <Script
          id="cloudflare-turnstile"
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
          strategy="afterInteractive"
          onReady={renderWidget}
        />
      )}
      <div ref={containerRef} />
      {hasError && (
        <p className="text-pink-primary text-sm" role="alert">
          {__("The security check could not be loaded. Please refresh the page and try again.", locale)}
        </p>
      )}
      {!hasError && (
        <p className="text-primary-light text-sm">{__("Complete the security check to send your message.", locale)}</p>
      )}
    </div>
  );
};

export default TurnstileWidget;
